/**
 * Round-index search — the console half of the search folding (PRD #330,
 * issue #333). Mirrors `fold` / `matches_query` in
 * src/vera/api/expediente_index.py; both sides are pinned by the shared
 * fixture tests/fixtures/search_folding_cases.json (see search.test.ts).
 *
 * The server stores `search_text` already folded; the console folds the QUERY
 * with the same rule and matches word-AND: every whitespace-separated folded
 * token must be a substring of `search_text` ("juan perez" finds
 * "perez lopez juan"). Queries are NOT underscore-normalised (tags are stored
 * with `_` → space, so "menor de edad" matches and "menor_de_edad" doesn't).
 */

/**
 * Where JS `toLowerCase` stops short of Python's `str.casefold` (full case
 * folding) for text that survives NFKD: `ß` → `ss` (`ẞ` lowercases to `ß`
 * first) and the final sigma `ς` → `σ` (`toLowerCase` even PRODUCES `ς` for a
 * word-final `Σ`). The other casefold-only mappings that matter here are
 * compatibility characters NFKD already rewrites (`ﬁ`, `ſ`, `µ`, full-width).
 * Known, accepted gap: Cherokee small letters (casefold maps them UP) and a
 * few archaic Cyrillic forms — irrelevant to Mexican documents.
 */
const CASEFOLD_EXTRA: Record<string, string> = { ß: "ss", ς: "σ" };
const CASEFOLD_EXTRA_RE = /[ßς]/g;

/** NFKD, drop every Unicode mark (`\p{M}`), casefold — the server's rule. */
export function fold(text: string): string {
  return text
    .normalize("NFKD")
    .replace(/\p{M}/gu, "")
    .toLowerCase()
    .replace(CASEFOLD_EXTRA_RE, (ch) => CASEFOLD_EXTRA[ch]);
}

function tokens(query: string): string[] {
  return fold(query).split(/\s+/).filter(Boolean);
}

/** Word-AND match of a raw query against an already-folded `search_text`.
 * An empty (or blank) query matches everything. */
export function matches(searchText: string, query: string): boolean {
  return compileQuery(query)(searchText);
}

/** `matches` with the query folded once — for filtering a whole round. */
export function compileQuery(query: string): (searchText: string) => boolean {
  const wanted = tokens(query);
  return (searchText) => wanted.every((token) => searchText.includes(token));
}

/** Minimum length before the search applies — a client convenience, not part
 * of the matching rule (one character matches nearly every row). */
export const MIN_QUERY_LENGTH = 2;

export function isActiveQuery(query: string): boolean {
  return query.trim().length >= MIN_QUERY_LENGTH;
}
